import React, { useState, useEffect, useRef } from 'react';
import { Play, Calendar, ShoppingCart, Star, Users, Video, Clock, MapPin, Phone, Mail, Check, Heart, Camera } from 'lucide-react';
// Import HLS.js for video streaming
// npm install hls.js
import Hls from 'hls.js';


export default function LiveStreamLanding() {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentView, setCurrentView] = useState('home'); // 'home', 'archive', 'packages', 'contact'
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Live streaming state
  const [isLive, setIsLive] = useState(false);
  const [viewerCount, setViewerCount] = useState(1247);
  const [streamUrl, setStreamUrl] = useState('');
  const videoRef = useRef(null);
  const pollInterval = useRef(null);

  // Contact form state
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', service: '' });
  const [formStatus, setFormStatus] = useState('');

  // Check stream status on component mount
  useEffect(() => {
    checkStreamStatus();
    
    // Poll for stream status every 10 seconds
    pollInterval.current = setInterval(checkStreamStatus, 10000);
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  // Simulate viewer count changes when live
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(() => {
        setViewerCount(prev => Math.max(1, prev + Math.floor(Math.random() * 20) - 10));
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isLive]);

  // Check if stream is currently live
  const checkStreamStatus = async () => {
    try {
      // Replace with your actual API endpoint
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_URL}/api/stream-status/current-event`);
      const data = await response.json();
      
      setIsLive(data.isLive);
      
      if (data.isLive && data.playbackUrl) {
        setStreamUrl(data.playbackUrl);
        initializePlayer(data.playbackUrl);
      }
    } catch (error) {
      console.error('Error checking stream status:', error);
      // For demo purposes, you can simulate a live stream
      // setIsLive(true);
    }
  };

  // Initialize HLS video player
  const hlsRef = useRef(null);


const initializePlayer = (url) => {
  if (!videoRef.current) return;

  // Clean up existing HLS instance
  if (hlsRef.current) {
    try {
      hlsRef.current.destroy();
    } catch (e) {
      console.log('Error destroying HLS:', e);
    }
    hlsRef.current = null;
  }

  if (Hls.isSupported()) {
    const hls = new Hls({
      debug: false,
      enableWorker: true,
      lowLatencyMode: true,
      backBufferLength: 90,
      liveSyncDurationCount: 3,        // Stay 3 segments from live edge
      liveMaxLatencyDurationCount: 10, // Max 10 segments behind
      liveDurationInfinity: true,
      highBufferWatchdogPeriod: 2,
    });
    
    hlsRef.current = hls;
    
    hls.loadSource(url);
    hls.attachMedia(videoRef.current);
    
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      console.log('HLS manifest parsed');
      videoRef.current.play().catch(err => {
        console.log('Autoplay prevented:', err);
      });
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      console.log('HLS error:', data.type, data.details);
      
      if (data.fatal) {
        switch(data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.log('Fatal network error, attempting recovery');
            setTimeout(() => {
              hls.startLoad();
            }, 1000);
            break;
            
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('Fatal media error, attempting recovery');
            hls.recoverMediaError();
            
            // If recovery fails, try swapping audio codec
            setTimeout(() => {
              if (hls.media && hls.media.error) {
                console.log('First recovery failed, trying swapAudioCodec');
                hls.swapAudioCodec();
                hls.recoverMediaError();
              }
            }, 2000);
            break;
            
          default:
            console.error('Unrecoverable error, reinitializing player');
            hls.destroy();
            hlsRef.current = null;
            
            // Attempt to reinitialize after 3 seconds
            setTimeout(() => {
              console.log('Reinitializing player...');
              initializePlayer(url);
            }, 3000);
            break;
        }
      }
    });

    // Jump to live edge when falling too far behind
    hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
      if (hls.media && hls.liveSyncPosition !== undefined) {
        const currentTime = hls.media.currentTime;
        const liveEdge = hls.liveSyncPosition;
        const delay = liveEdge - currentTime;
        
        // If more than 10 seconds behind, jump to live edge
        if (delay > 10) {
          console.log(`Too far behind (${delay.toFixed(1)}s), jumping to live edge`);
          hls.media.currentTime = liveEdge - 2; // 2 seconds from live edge
        }
      }
    });

  } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
    // Safari native HLS
    videoRef.current.src = url;
    videoRef.current.addEventListener('loadedmetadata', () => {
      videoRef.current.play();
    });
  }
};

// Cleanup on unmount
useEffect(() => {
  return () => {
    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch (e) {
        console.log('Cleanup error:', e);
      }
    }
  };
}, []);

  // Contact form handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      // Using Web3Forms (free email service)
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '116746df-1b23-48fe-a46d-a4d7c41b0e2f', // Get from web3forms.com
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          service: formData.service,
          to: 'chester4967@aol.com',
          subject: `LiveVideo2Go Inquiry - ${formData.service || 'General'}`
        })
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '', service: '' });
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Package data
  const funeralPackages = [
    {
      name: 'Essential Memorial',
      price: '$195',
      description: 'Perfect for intimate services',
      features: [
        'Live stream broadcast (up to 2 hours)',
        '7-day replay access for family',
        'Up to 50 concurrent viewers',
        'Email support',
        'Private stream link'
      ],
      icon: Heart,
      popular: false
    },
    {
      name: 'Family & Friends',
      price: '$295',
      description: 'Share memories with loved ones',
      features: [
        'Live stream broadcast (up to 3 hours)',
        '30-day replay access',
        'Up to 100 concurrent viewers',
        'Download video file (MP4)',
        'Email & phone support',
        'Recording delivery'
      ],
      icon: Users,
      popular: true
    },
    {
      name: 'Legacy Tribute',
      price: '$495',
      description: 'Preserve memories forever',
      features: [
        'Live stream broadcast (up to 4 hours)',
        'Unlimited replay access (1 year)',
        'Unlimited concurrent viewers',
        'Full HD download + cloud backup',
        'Edited highlights video (10 min)',
        'Priority phone support',
        'USB drive mailed to family',
        'Multi-camera option available'
      ],
      icon: Star,
      popular: false
    }
  ];

  const traditionalPackages = [
    {
      name: 'Celebration',
      price: '$350',
      description: 'Birthdays, anniversaries, parties',
      features: [
        'Live stream (up to 4 hours)',
        '14-day replay access',
        'Up to 75 concurrent viewers',
        'Download option',
        'Email support'
      ],
      icon: Calendar
    },
    {
      name: 'Professional Event',
      price: '$550',
      description: 'Conferences, graduations, galas',
      features: [
        'Live stream (up to 6 hours)',
        '60-day replay access',
        'Up to 200 concurrent viewers',
        'Full HD download',
        'Phone & email support',
        'Multi-angle option',
        'Professional audio'
      ],
      icon: Camera
    }
  ];

  const upcomingEvents = [
    {
      id: 1,
      title: "Thompson Memorial Service",
      date: "November 28, 2025",
      time: "2:00 PM EST",
      location: "Pittsburgh, PA",
      price: "$19.99",
      status: "past", // past, upcoming, live
      type: "Memorial"
    },
    {
      id: 2,
      title: "Johnson 50th Anniversary Celebration",
      date: "January 15, 2026",
      time: "6:00 PM EST",
      location: "McKees Rocks, PA",
      price: "$29.99",
      status: "upcoming",
      type: "Anniversary"
    },
    {
      id: 3,
      title: "Martinez Family Reunion",
      date: "January 22, 2026",
      time: "2:00 PM EST",
      location: "Pittsburgh, PA",
      price: "$24.99",
      status: "upcoming",
      type: "Party"
    },
    {
      id: 4,
      title: "Brown Birthday Celebration",
      date: "December 5, 2025",
      time: "4:00 PM EST",
      location: "McKees Rocks, PA",
      price: "$24.99",
      status: "past",
      type: "Celebration"
    }
  ];

  const pastVideos = [
    {
      id: 1,
      title: "Smith Wedding Celebration",
      date: "November 10, 2025",
      duration: "3:45:20",
      views: "2,847",
      thumbnail: "wedding",
      price: "$34.99",
      rating: 5,
      category: "Wedding"
    },
    {
      id: 2,
      title: "Tech Summit 2025",
      date: "November 5, 2025",
      duration: "6:12:30",
      views: "5,234",
      thumbnail: "conference",
      price: "$49.99",
      rating: 5,
      category: "Conference"
    },
    {
      id: 3,
      title: "Birthday Bash Extravaganza",
      date: "October 28, 2025",
      duration: "2:30:15",
      views: "1,456",
      thumbnail: "party",
      price: "$24.99",
      rating: 4,
      category: "Party"
    },
    {
      id: 4,
      title: "Annual Charity Gala",
      date: "October 20, 2025",
      duration: "4:15:45",
      views: "3,892",
      thumbnail: "gala",
      price: "$39.99",
      rating: 5,
      category: "Gala"
    },
    {
      id: 5,
      title: "Rodriguez 25th Anniversary",
      date: "October 15, 2025",
      duration: "3:20:10",
      views: "2,134",
      thumbnail: "anniversary",
      price: "$29.99",
      rating: 5,
      category: "Anniversary"
    },
    {
      id: 6,
      title: "Summer Music Festival",
      date: "October 8, 2025",
      duration: "5:45:00",
      views: "8,567",
      thumbnail: "festival",
      price: "$44.99",
      rating: 5,
      category: "Festival"
    },
    {
      id: 7,
      title: "Corporate Training Seminar",
      date: "September 30, 2025",
      duration: "4:00:00",
      views: "1,892",
      thumbnail: "seminar",
      price: "$39.99",
      rating: 4,
      category: "Corporate"
    },
    {
      id: 8,
      title: "Kids Birthday Pool Party",
      date: "September 22, 2025",
      duration: "2:15:30",
      views: "987",
      thumbnail: "kids",
      price: "$19.99",
      rating: 5,
      category: "Party"
    },
    {
      id: 9,
      title: "Graduation Ceremony 2025",
      date: "September 15, 2025",
      duration: "3:30:00",
      views: "4,234",
      thumbnail: "graduation",
      price: "$34.99",
      rating: 5,
      category: "Graduation"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, #FFB81C 0px, #FFB81C 2px, transparent 2px, transparent 20px)`,
          animation: 'slidePattern 20s linear infinite'
        }}></div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Oswald:wght@400;600;700&family=Barlow:wght@400;600;700&display=swap');
        
        @keyframes slidePattern {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(20px) translateY(20px); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        .animate-slideUp {
          animation: slideUp 0.8s ease-out forwards;
        }
        
        .live-indicator {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .shimmer {
          background: linear-gradient(90deg, transparent, rgba(255, 184, 28, 0.3), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
      `}</style>

      {/* Header */}
      <header className="relative z-20 border-b-4 border-yellow-400">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
        
              <div>
              <p className="text-xs text-gray-400 tracking-wide" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <img src="logo.png" alt="Live Video 2 Go"></img>
                  LIVE STREAMING EXCELLENCE
                </p>
              </div>
            </div>
            
            {/* Navigation Menu */}
            <nav className="hidden md:flex items-center gap-8">
              <button 
                onClick={() => setCurrentView('home')}
                className={`font-bold transition-all ${currentView === 'home' ? 'text-yellow-400 scale-110' : 'text-gray-300 hover:text-yellow-400'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                HOME
              </button>
              <button 
                onClick={() => setCurrentView('archive')}
                className={`font-bold transition-all ${currentView === 'archive' ? 'text-yellow-400 scale-110' : 'text-gray-300 hover:text-yellow-400'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                PAST EVENTS
              </button>
              <button 
                onClick={() => setCurrentView('packages')}
                className={`font-bold transition-all ${currentView === 'packages' ? 'text-yellow-400 scale-110' : 'text-gray-300 hover:text-yellow-400'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                PACKAGES
              </button>
              <button 
                onClick={() => setCurrentView('contact')}
                className={`font-bold transition-all ${currentView === 'contact' ? 'text-yellow-400 scale-110' : 'text-gray-300 hover:text-yellow-400'}`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                CONTACT
              </button>
            </nav>

            <div className="flex items-center gap-3">
              <a 
                href="tel:4123779238"
                className="hidden md:inline-block px-6 py-3 bg-yellow-400 text-black font-bold rounded hover:bg-yellow-300 transition-all hover:scale-105 shadow-lg shadow-yellow-400/50" 
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                CALL TO BOOK
              </a>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center w-11 h-11 border-2 border-yellow-400/60 rounded-lg text-yellow-400 hover:border-yellow-400 hover:text-yellow-300 transition-all"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              >
                <div className="flex flex-col gap-1.5">
                  <span className={`block h-0.5 w-6 bg-current transition-transform ${isMobileMenuOpen ? 'translate-y-2 rotate-45' : ''}`}></span>
                  <span className={`block h-0.5 w-6 bg-current transition-opacity ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block h-0.5 w-6 bg-current transition-transform ${isMobileMenuOpen ? '-translate-y-2 -rotate-45' : ''}`}></span>
                </div>
              </button>
            </div>
          </div>
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 border-2 border-yellow-400/40 rounded-xl bg-gradient-to-br from-gray-900 to-black p-4">
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setCurrentView('home');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left font-bold transition-all ${currentView === 'home' ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  HOME
                </button>
                <button
                  onClick={() => {
                    setCurrentView('archive');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left font-bold transition-all ${currentView === 'archive' ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  PAST EVENTS
                </button>
                <button
                  onClick={() => {
                    setCurrentView('packages');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left font-bold transition-all ${currentView === 'packages' ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  PACKAGES
                </button>
                <button
                  onClick={() => {
                    setCurrentView('contact');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left font-bold transition-all ${currentView === 'contact' ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`}
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  CONTACT
                </button>
                <a
                  href="tel:4123779238"
                  className="mt-2 inline-flex items-center justify-center px-4 py-3 bg-yellow-400 text-black font-bold rounded hover:bg-yellow-300 transition-all"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  CALL TO BOOK
                </a>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Conditional View Rendering */}
      {currentView === 'home' ? (
        <>
      {/* Hero Section with Video Player */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Video Player */}
          <div className="animate-slideUp">
            <div className="relative group">
              {/* Video Container */}
              <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden border-4 border-yellow-400 shadow-2xl shadow-yellow-400/20">
                {/* Aspect Ratio Container */}
                <div className="aspect-video relative">
                  {/* Conditional rendering based on live status */}
                  {isLive ? (
                    /* Live Video Player */
                    <video
                      ref={videoRef}
                      className="absolute inset-0 w-full h-full object-cover"
                      controls
                      autoPlay
                      muted
                    >
                      <source src={streamUrl} type="application/x-mpegURL" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    /* Offline Placeholder */
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 mx-auto mb-4 bg-gray-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform cursor-pointer">
                          <Play className="w-12 h-12 text-gray-500 ml-1" />
                        </div>
                        <p className="text-xl font-bold text-gray-400" style={{ fontFamily: "'Oswald', sans-serif" }}>
                          STREAM OFFLINE
                        </p>
                        <p className="text-sm text-gray-500 mt-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                          Check back soon for the next live event
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Live Indicator - Only show when actually streaming */}
                  {isLive && (
                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full live-indicator z-10">
                      <div className="w-3 h-3 bg-white rounded-full"></div>
                      <span className="text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>LIVE</span>
                    </div>
                  )}
                  
                  {/* Viewer Count - Only show when live */}
                  {isLive && (
                    <div className="absolute top-4 right-4 bg-black/80 px-4 py-2 rounded-full flex items-center gap-2 z-10">
                      <Users className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>{viewerCount.toLocaleString()} watching</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Recording Purchase CTA */}
              <div className="mt-6 bg-gradient-to-r from-yellow-400 to-yellow-500 p-6 rounded-xl text-black">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-black mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      OWN THIS MOMENT FOREVER
                    </h3>
                    <p className="text-sm font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      Purchase HD recording after the event
                    </p>
                  </div>
                 <a 
  href="tel:4123779238"
  className="px-6 py-3 bg-black text-yellow-400 font-bold rounded-lg hover:bg-gray-900 transition-all flex items-center gap-2 shadow-lg" 
  style={{ fontFamily: "'Oswald', sans-serif" }}
>
  <Phone className="w-5 h-5" />
  CALL TO PURCHASE
</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Hero Content */}
          <div className="animate-slideUp" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <div className="mb-6">
              <div className="inline-block px-4 py-2 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mb-4">
                <span className="text-yellow-400 font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  PROFESSIONAL LIVE STREAMING
                </span>
              </div>
              <h2 className="text-6xl lg:text-7xl font-black leading-none mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                STREAM YOUR
                <span className="block text-yellow-400">SPECIAL MOMENTS</span>
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8" style={{ fontFamily: "'Barlow', sans-serif" }}>
                Professional live streaming for memorials, funerals, parties, anniversaries, and every celebration that matters. Share your moments with compassion and clarity.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: Video, title: "HD Quality", desc: "Crystal clear streaming" },
                { icon: Calendar, title: "Easy Booking", desc: "Simple scheduling" },
                { icon: Star, title: "Pro Setup", desc: "Expert support" },
                { icon: ShoppingCart, title: "Get Recordings", desc: "Own your memories" }
              ].map((feature, idx) => (
                <div 
                  key={idx}
                  className="bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg p-4 hover:border-yellow-400 transition-all hover:scale-105"
                  style={{ animationDelay: `${0.3 + idx * 0.1}s` }}
                >
                  <feature.icon className="w-8 h-8 text-yellow-400 mb-2" />
                  <h4 className="font-bold text-sm mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {feature.title}
                  </h4>
                  <p className="text-xs text-gray-400" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>

            <button 
              onClick={() => setCurrentView('contact')}
              className="w-full py-4 bg-yellow-400 text-black text-xl font-black rounded-lg hover:bg-yellow-300 transition-all hover:scale-105 shadow-2xl shadow-yellow-400/50 shimmer" 
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              START YOUR STREAM TODAY
            </button>
          </div>
        </div>
      </section>

      {/* Upcoming Events Schedule */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mb-4">
            <span className="text-yellow-400 font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
              RECENT & UPCOMING
            </span>
          </div>
          <h2 className="text-5xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            OUR <span className="text-yellow-400">EVENTS</span>
          </h2>
          <p className="text-gray-400 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>
            View recent recordings or see what's coming up
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {upcomingEvents.map((event, idx) => (
            <div
              key={event.id}
              className="group bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-xl overflow-hidden hover:border-yellow-400 transition-all hover:scale-105 cursor-pointer"
              onClick={() => setSelectedEvent(event)}
              style={{ 
                animation: 'slideUp 0.6s ease-out forwards',
                animationDelay: `${idx * 0.1}s`,
                opacity: 0
              }}
            >
              {/* Event Header */}
              <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 p-4 relative overflow-hidden">
                <div className="absolute inset-0 shimmer"></div>
                <div className="relative flex justify-between items-start">
                  <div>
                    <span className="inline-block px-3 py-1 bg-black text-yellow-400 text-xs font-bold rounded-full mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {event.type.toUpperCase()}
                    </span>
                    <h3 className="text-xl font-black text-black" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {event.title}
                    </h3>
                  </div>
                  {event.status === "live" && (
                    <div className="flex items-center gap-1 bg-red-600 px-3 py-1 rounded-full">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-xs font-bold text-white" style={{ fontFamily: "'Oswald', sans-serif" }}>LIVE</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Event Details */}
              <div className="p-6">
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Calendar className="w-5 h-5 text-yellow-400" />
                    <span className="font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {event.date}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    <span className="font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {event.time}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <MapPin className="w-5 h-5 text-yellow-400" />
                    <span className="font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {event.location}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                  <div>
                    <p className="text-xs text-gray-500 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {event.status === "past" ? "Recording Price" : "Price"}
                    </p>
                    <p className="text-2xl font-black text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {event.price}
                    </p>
                  </div>
                  {event.status === "past" && (
  <a 
    href="tel:4123779238"
    className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-all group-hover:scale-110 flex items-center gap-2" 
    style={{ fontFamily: "'Oswald', sans-serif" }}
  >
    <Phone className="w-4 h-4" />
    CALL TO PURCHASE
  </a>
)}
                  {event.status === "live" && (
                    <button className="px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-500 transition-all group-hover:scale-110 flex items-center gap-2 animate-pulse" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <Play className="w-4 h-4" />
                      WATCH LIVE
                    </button>
                  )}
                  {/* No button for upcoming events - just show info */}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 rounded-2xl p-12 text-center text-black relative overflow-hidden">
          <div className="absolute inset-0 shimmer"></div>
          <div className="relative">
            <h2 className="text-5xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              READY TO GO LIVE?
            </h2>
            <p className="text-xl mb-8 font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Book your live stream event today and share your special moments with the world
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button 
                onClick={() => setCurrentView('contact')}
                className="px-8 py-4 bg-black text-yellow-400 text-lg font-black rounded-lg hover:bg-gray-900 transition-all hover:scale-105 shadow-2xl" 
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                SCHEDULE YOUR EVENT
              </button>
              <button 
                onClick={() => setCurrentView('packages')}
                className="px-8 py-4 bg-white text-black text-lg font-black rounded-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-2xl" 
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                VIEW PACKAGES
              </button>
            </div>
          </div>
        </div>
      </section>
      </>
      ) : currentView === 'archive' ? (
        /* Archive View - Past Videos */
        <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          {/* Archive Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mb-4">
              <span className="text-yellow-400 font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                VIDEO ARCHIVE
              </span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              PAST <span className="text-yellow-400">EVENTS</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Relive the magic! Purchase professional HD recordings of our past live streamed events
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {['All', 'Wedding', 'Party', 'Conference', 'Anniversary', 'Gala'].map((filter) => (
              <button
                key={filter}
                className="px-6 py-2 bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-full font-bold hover:border-yellow-400 hover:bg-yellow-400 hover:text-black transition-all"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {filter.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Videos Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pastVideos.map((video, idx) => (
              <div
                key={video.id}
                className="group bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-xl overflow-hidden hover:border-yellow-400 transition-all hover:scale-105 cursor-pointer"
                style={{ 
                  animation: 'slideUp 0.6s ease-out forwards',
                  animationDelay: `${idx * 0.05}s`,
                  opacity: 0
                }}
              >
                {/* Video Thumbnail */}
                <div className="relative aspect-video bg-gradient-to-br from-gray-800 via-gray-900 to-black overflow-hidden">
                  {/* Thumbnail Placeholder with Category-specific gradient */}
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-yellow-400/20 to-transparent">
                    <div className="text-center">
                      <Play className="w-16 h-16 text-yellow-400 mx-auto mb-2 opacity-80 group-hover:scale-125 transition-transform" />
                      <p className="text-xs text-gray-500 font-bold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                        {video.category}
                      </p>
                    </div>
                  </div>
                  
                  {/* Duration Badge */}
                  <div className="absolute bottom-3 right-3 bg-black/90 px-3 py-1 rounded">
                    <span className="text-xs font-bold text-yellow-400" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {video.duration}
                    </span>
                  </div>

                  {/* Views Badge */}
                  <div className="absolute top-3 right-3 bg-black/90 px-3 py-1 rounded flex items-center gap-1">
                    <Users className="w-3 h-3 text-yellow-400" />
                    <span className="text-xs font-bold text-gray-300" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {video.views}
                    </span>
                  </div>

                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 bg-yellow-400 px-3 py-1 rounded">
                    <span className="text-xs font-bold text-black" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {video.category.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-5">
                  <h3 className="text-xl font-black mb-2 text-white group-hover:text-yellow-400 transition-colors" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                    {video.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-4 h-4 text-yellow-400" />
                    <span className="text-sm text-gray-400" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      {video.date}
                    </span>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < video.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
                      />
                    ))}
                    <span className="text-xs text-gray-500 ml-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                      ({video.rating}.0)
                    </span>
                  </div>

                  {/* Price and Purchase */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                    <div>
                      <p className="text-xs text-gray-500 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>
                        Purchase HD
                      </p>
                      <p className="text-2xl font-black text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                        {video.price}
                      </p>
                    </div>
                  <a 
  href="tel:4123779238"
  className="px-5 py-2 bg-yellow-400 text-black font-bold rounded hover:bg-yellow-300 transition-all flex items-center gap-2 group-hover:scale-110 shadow-lg shadow-yellow-400/30" 
  style={{ fontFamily: "'Oswald', sans-serif" }}
>
  <Phone className="w-4 h-4" />
  CALL
</a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load More Button */}
          <div className="text-center mt-12">
            <button className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black text-lg font-black rounded-lg hover:from-yellow-300 hover:to-yellow-400 transition-all hover:scale-105 shadow-2xl shadow-yellow-400/50" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              LOAD MORE VIDEOS
            </button>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t-2 border-yellow-400/30">
            {[
              { label: 'Total Events', value: '150+' },
              { label: 'Happy Clients', value: '500+' },
              { label: 'Hours Streamed', value: '1,000+' },
              { label: 'HD Recordings', value: '200+' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="text-4xl lg:text-5xl font-black text-yellow-400 mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {stat.value}
                </p>
                <p className="text-sm text-gray-400 font-semibold" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : currentView === 'packages' ? (
        /* Packages View */
        <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mb-4">
              <span className="text-yellow-400 font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                AFFORDABLE PRICING
              </span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              STREAMING <span className="text-yellow-400">PACKAGES</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Compassionate, professional service for every occasion
            </p>
          </div>

          {/* Memorial Services Packages */}
          <div className="mb-16">
            <h3 className="text-3xl font-black text-center mb-8 flex items-center justify-center gap-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <Heart className="w-8 h-8 text-yellow-400" />
              MEMORIAL & FUNERAL SERVICES
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {funeralPackages.map((pkg, idx) => (
                <div 
                  key={idx} 
                  className={`bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden border-2 ${pkg.popular ? 'border-yellow-400 shadow-2xl shadow-yellow-400/20 transform scale-105' : 'border-yellow-400/30'} hover:border-yellow-400 transition-all`}
                  style={{ 
                    animation: 'slideUp 0.6s ease-out forwards',
                    animationDelay: `${idx * 0.1}s`,
                    opacity: 0
                  }}
                >
                  {pkg.popular && (
                    <div className="bg-yellow-400 text-black text-center py-2 font-bold text-sm" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      MOST POPULAR
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <pkg.icon className="w-10 h-10 text-yellow-400" />
                      <span className="text-4xl font-black text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{pkg.price}</span>
                    </div>
                    <h4 className="text-2xl font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{pkg.name}</h4>
                    <p className="text-gray-400 mb-6 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>{pkg.description}</p>
                    <ul className="space-y-3 mb-6">
                      {pkg.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-base" style={{ fontFamily: "'Barlow', sans-serif" }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => setCurrentView('contact')}
                      className="w-full bg-yellow-400 text-black py-3 rounded-lg font-bold hover:bg-yellow-300 transition-all text-lg" 
                      style={{ fontFamily: "'Oswald', sans-serif" }}
                    >
                      SELECT PACKAGE
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traditional Events Packages */}
          <div className="mb-16">
            <h3 className="text-3xl font-black text-center mb-8 flex items-center justify-center gap-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <Calendar className="w-8 h-8 text-yellow-400" />
              CELEBRATIONS & EVENTS
            </h3>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {traditionalPackages.map((pkg, idx) => (
                <div 
                  key={idx} 
                  className="bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 border-2 border-yellow-400/30 hover:border-yellow-400 transition-all"
                  style={{ 
                    animation: 'slideUp 0.6s ease-out forwards',
                    animationDelay: `${idx * 0.1}s`,
                    opacity: 0
                  }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <pkg.icon className="w-10 h-10 text-yellow-400" />
                    <span className="text-4xl font-black text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{pkg.price}</span>
                  </div>
                  <h4 className="text-2xl font-black mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{pkg.name}</h4>
                  <p className="text-gray-400 mb-6 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>{pkg.description}</p>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300 text-base" style={{ fontFamily: "'Barlow', sans-serif" }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button 
                    onClick={() => setCurrentView('contact')}
                    className="w-full bg-yellow-400 text-black py-3 rounded-lg font-bold hover:bg-yellow-300 transition-all text-lg" 
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    SELECT PACKAGE
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Package CTA */}
          <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 rounded-2xl p-12 text-center text-black relative overflow-hidden">
            <div className="absolute inset-0 shimmer"></div>
            <div className="relative">
              <h3 className="text-4xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>NEED SOMETHING CUSTOM?</h3>
              <p className="text-xl mb-6 font-semibold max-w-2xl mx-auto" style={{ fontFamily: "'Barlow', sans-serif" }}>
                Every service is unique. We're happy to create a custom package tailored to your specific needs and budget.
              </p>
              <button 
                onClick={() => setCurrentView('contact')}
                className="px-10 py-4 bg-black text-yellow-400 text-lg font-black rounded-lg hover:bg-gray-900 transition-all hover:scale-105 shadow-2xl" 
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                REQUEST CUSTOM QUOTE
              </button>
            </div>
          </div>
        </section>
      ) : (
        /* Contact View */
        <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mb-4">
              <span className="text-yellow-400 font-bold text-sm tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
                LET'S CONNECT
              </span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-black mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              GET IN <span className="text-yellow-400">TOUCH</span>
            </h2>
            <p className="text-gray-400 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>
              We're here to help with compassionate, professional service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl font-black mb-6 text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Contact Information</h3>
                
                <div className="space-y-4">
                  <a 
                    href="tel:4123779238" 
                    className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg hover:border-yellow-400 transition-all group"
                  >
                    <Phone className="w-6 h-6 text-yellow-400 mt-1 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-gray-400 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>Phone</p>
                      <p className="text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>(412) 377-9238</p>
                    </div>
                  </a>

                  <a 
                    href="mailto:chester4967@aol.com" 
                    className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg hover:border-yellow-400 transition-all group"
                  >
                    <Mail className="w-6 h-6 text-yellow-400 mt-1 group-hover:scale-110 transition-transform" />
                    <div>
                      <p className="text-gray-400 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>Email</p>
                      <p className="text-xl font-bold break-all" style={{ fontFamily: "'Oswald', sans-serif" }}>chester4967@aol.com</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg">
                    <MapPin className="w-6 h-6 text-yellow-400 mt-1" />
                    <div>
                      <p className="text-gray-400 text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>Address</p>
                      <p className="text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>24 Furnace Street</p>
                      <p className="text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>McKees Rocks, PA 15136</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-lg p-6 border-l-4 border-yellow-400">
                <h4 className="text-xl font-black mb-3 text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Why Choose LiveVideo2Go?</h4>
                <ul className="space-y-2 text-gray-300" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span>Family-owned and operated in Pittsburgh</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span>Compassionate service during difficult times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span>Reliable, professional live streaming</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span>Affordable packages for every budget</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <span>Dedicated support before, during, and after</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h3 className="text-3xl font-black mb-6 text-yellow-400" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm text-gray-400 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-all text-lg"
                    placeholder="Your name"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm text-gray-400 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-all text-lg"
                    placeholder="your@email.com"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm text-gray-400 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-all text-lg"
                    placeholder="(412) 555-1234"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm text-gray-400 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Service Type</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-all text-lg"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    <option value="">Select a service...</option>
                    <option value="memorial">Memorial/Funeral Service</option>
                    <option value="celebration">Celebration/Anniversary</option>
                    <option value="professional">Professional Event</option>
                    <option value="custom">Custom Package</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm text-gray-400 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="5"
                    className="w-full bg-gradient-to-br from-gray-900 to-black border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-all text-lg resize-none"
                    placeholder="Tell us about your needs..."
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-yellow-400 text-black py-4 rounded-lg font-bold hover:bg-yellow-300 transition-all text-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow-400/50"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                >
                  {formStatus === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
                </button>

                {formStatus === 'success' && (
                  <div className="bg-green-900/50 border-2 border-green-500 rounded-lg p-4 text-center">
                    <p className="text-green-300 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>Thank you! We'll be in touch soon.</p>
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-900/50 border-2 border-red-500 rounded-lg p-4 text-center">
                    <p className="text-red-300 text-lg" style={{ fontFamily: "'Barlow', sans-serif" }}>Something went wrong. Please call us instead.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="relative z-10 border-t-4 border-yellow-400 bg-black py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400" style={{ fontFamily: "'Barlow', sans-serif" }}>
            © 2026 LiveVideo2Go - Professional Live Streaming Services
          </p>
          <p className="text-sm text-gray-600 mt-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Serving McKees Rocks and the greater Pittsburgh area with compassionate, reliable streaming for memorials, funerals, and celebrations.
          </p>
        </div>
      </footer>
    </div>
  );
}
